import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format, formatDistanceToNow } from "date-fns";
import { 
  Check, ChevronsUpDown, LayoutGrid, Pencil, RefreshCw, RotateCcw, 
  Table as TableIcon, Search, CalendarPlus, Phone, User
} from "lucide-react";
import { Modal } from "../../components/ui/modal";
import { DateTimePicker } from "../../components/ui/date-picker";
import { initialsOf, avatarColorOf } from "./types";
import { Popover as _Popover, PopoverContent as _PopoverContent, PopoverTrigger as _PopoverTrigger } from "../../components/ui/popover";
import { Command as _Command, CommandEmpty as _CommandEmpty, CommandGroup as _CommandGroup, CommandInput as _CommandInput, CommandItem as _CommandItem, CommandList as _CommandList } from "../../components/ui/command";
import { Select as _Select, SelectContent as _SelectContent, SelectItem as _SelectItem, SelectTrigger as _SelectTrigger, SelectValue as _SelectValue } from "../../components/ui/select";
import { cn } from "../../lib/utils";
import { crmApi } from "./api";
import type { CrmFollowUp, Lead } from "./types";
import { LeadCallButton } from "./lead-call-button";

const Popover = _Popover as any;
const PopoverContent = _PopoverContent as any;
const PopoverTrigger = _PopoverTrigger as any;
const Command = _Command as any;
const CommandEmpty = _CommandEmpty as any;
const CommandGroup = _CommandGroup as any;
const CommandInput = _CommandInput as any;
const CommandItem = _CommandItem as any;
const CommandList = _CommandList as any;
const Select = _Select as any;
const SelectContent = _SelectContent as any;
const SelectItem = _SelectItem as any;
const SelectTrigger = _SelectTrigger as any;
const SelectValue = _SelectValue as any;

const ALL = "__all__";

const localTime = (iso: string) => { 
  const d = new Date(iso); 
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); 
};

export function followUpStatus(row: CrmFollowUp, now = new Date()) {
  if (row.done) return "Done";
  const due = new Date(row.dueAt);
  if (due < now) return "Overdue";
  return due.toDateString() === now.toDateString() ? "Today" : "Upcoming";
}

const TABS = ["All", "Overdue", "Today", "Upcoming", "Done"] as const;

function LeadCombobox({ leads, value, onChange, disabled }: { leads: Lead[], value: string, onChange: (v: string) => void, disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const selected = leads.find(l => l.id === value);
  const displayName = selected ? ([selected.name, selected.phone].filter(Boolean).join(" · ") || selected.contactId || selected.id) : "Choose contact";

  return (
    <Popover open={open && !disabled} onOpenChange={(v: boolean) => !disabled && setOpen(v)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "flex w-full items-center justify-between gap-2 rounded-lg border border-line bg-transparent px-3 py-2 text-xs text-ink outline-none",
            disabled ? "cursor-not-allowed opacity-60" : "hover:bg-soft focus:border-brand"
          )}
        >
          <span className={cn("truncate", !selected && "text-ink-3")}>{displayName}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[340px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search contacts…" />
          <CommandList className="max-h-[200px]">
            <CommandEmpty>No contacts found.</CommandEmpty>
            <CommandGroup>
              {leads.map(lead => {
                const label = [lead.name, lead.phone].filter(Boolean).join(" · ") || lead.contactId || lead.id;
                return (
                  <CommandItem
                    key={lead.id}
                    value={label}
                    onSelect={() => { onChange(lead.id); setOpen(false); }}
                    className="flex items-center gap-2 data-[selected=true]:bg-soft data-[selected=true]:text-ink text-xs"
                  >
                    <Check className={cn("h-3.5 w-3.5 shrink-0", value === lead.id ? "opacity-100" : "opacity-0")} />
                    <span className="truncate">{label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function FollowUpsPage() {
  const cache = useQueryClient();
  const navigate = useNavigate();
  const query = useQuery({ queryKey: ["crm-follow-ups"], queryFn: crmApi.followUps, refetchInterval: 15000 });
  const leads = useQuery({ queryKey: ["crm-follow-up-leads"], queryFn: crmApi.leads });
  const team = useQuery({ queryKey: ["crm-team"], queryFn: crmApi.team });

  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [agentFilter, setAgentFilter] = useState(ALL);
  const [viewMode, setViewMode] = useState<"table" | "board">(() => {
    try { return window.localStorage.getItem("crm-followups-view") === "board" ? "board" : "table"; } catch { return "table"; }
  });
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropCol, setDropCol] = useState<string | null>(null);

  const switchView = (mode: "table" | "board") => {
    setViewMode(mode);
    try { window.localStorage.setItem("crm-followups-view", mode); } catch { /* ignore */ }
  };

  const [editing, setEditing] = useState<CrmFollowUp | "new" | null>(null);
  const [leadId, setLeadId] = useState("");
  const [agentId, setAgentId] = useState("");
  const [due, setDue] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const rows = useMemo(() => query.data || [], [query.data]);
  const allLeads = useMemo(() => leads.data || [], [leads.data]);
  const members = useMemo(() => team.data?.members || [], [team.data?.members]);
  
  const memberMap = useMemo(() => new Map(members.map((m: any) => [m.id, m.name])), [members]);

  const agentNameOf = useCallback((id?: string | null) => {
    if (!id) return "Unassigned";
    const found = memberMap.get(id);
    if (found && !/^[0-9a-f-]{25,}$/i.test(found)) return found;
    // The user was removed/deactivated from the organization
    return "Former agent";
  }, [memberMap]);

  const openRecord = (leadId: string) => navigate(`/crm/leads/${leadId}`);

  const overdueCount = rows.filter(r => followUpStatus(r) === "Overdue").length;

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter(row => {
        // Tab status filter
        if (filter !== "All" && followUpStatus(row) !== filter) return false;

        // Agent filter
        const targetAgent = row.assignedAgentId || "unassigned";
        if (agentFilter !== ALL && targetAgent !== agentFilter) return false;

        // Search query
        if (q) {
          const leadName = (row.name || "").toLowerCase();
          const phone = (row.phone || "").toLowerCase();
          const notes = (row.note || "").toLowerCase();
          const agentName = agentNameOf(row.assignedAgentId).toLowerCase();
          if (!leadName.includes(q) && !phone.includes(q) && !notes.includes(q) && !agentName.includes(q)) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => Number(a.done) - Number(b.done) || new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime());
  }, [rows, filter, search, agentFilter, agentNameOf]);

  const BOARD_COLUMNS = ["Overdue", "Today", "Upcoming", "Done"] as const;
  const boardGroups = BOARD_COLUMNS.map((status) => ({
    status,
    items: visibleRows.filter((row) => followUpStatus(row) === status),
  }));

  const dueForColumn = (col: string): string => {
    const now = new Date();
    if (col === "Today") {
      const d = new Date(now);
      d.setHours(18, 0, 0, 0);
      if (d.getTime() <= now.getTime()) d.setTime(now.getTime() + 60 * 60 * 1000);
      return d.toISOString();
    }
    if (col === "Tomorrow" || col === "Upcoming") {
      const d = new Date(now);
      d.setDate(d.getDate() + 1);
      d.setHours(11, 0, 0, 0);
      return d.toISOString();
    }
    if (col === "Overdue") {
      const d = new Date(now);
      d.setDate(d.getDate() - 1);
      d.setHours(10, 0, 0, 0);
      return d.toISOString();
    }
    return now.toISOString();
  };

  const moveFollowUpToColumn = async (id: string, col: string) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    setBusy(true);
    try {
      if (col === "Done") {
        await crmApi.editFollowUp(row.leadId, row.id, { done: true });
      } else {
        await crmApi.editFollowUp(row.leadId, row.id, {
          done: false,
          dueAt: dueForColumn(col),
        });
      }
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      setDropCol(null);
      setDragId(null);
    }
  };

  const refresh = async () => {
    await cache.invalidateQueries({ queryKey: ["crm-follow-ups"] });
  };

  const setDone = async (row: CrmFollowUp, done: boolean) => {
    setBusy(true);
    try {
      await crmApi.editFollowUp(row.leadId, row.id, { done });
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const open = (row: CrmFollowUp | "new") => {
    setEditing(row);
    setLeadId(row === "new" ? "" : row.leadId);
    setAgentId(row === "new" ? "" : row.assignedAgentId || "");
    setDue(row === "new" ? "" : localTime(row.dueAt));
    setNote(row === "new" ? "" : row.note);
    setError("");
  };

  return (
    <div className="flex h-full flex-col p-4 sm:p-5 md:p-6 overflow-hidden">
      {/* ── Single Unified Toolbar: Title + Tabs + Search + Agent Filter + Board/Table Switcher + Refresh + Schedule Follow-up ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 shrink-0 border-b border-line pb-2.5">
        {/* Left: Title + Status Tabs */}
        <div className="flex items-center overflow-x-auto gap-2.5">
          <div className="flex items-center gap-2 shrink-0 pr-2 border-r border-line">
            <h1 className="text-sm font-semibold text-ink">Follow-ups</h1>
            <span className="font-mono text-xs rounded-full bg-soft px-2 py-0.5 text-ink-3">
              {rows.length}
            </span>
            {overdueCount > 0 && (
              <span className="font-mono text-xs rounded-full bg-red-500/10 text-red-600 dark:text-red-400 px-1.5 py-0.5 font-medium">
                {overdueCount} overdue
              </span>
            )}
          </div>

          <div className="flex items-center gap-1">
            {TABS.map(tab => {
              const count = rows.filter(row => tab === "All" || followUpStatus(row) === tab).length;
              const isActive = filter === tab;
              return (
                <button
                  key={tab}
                  onClick={() => setFilter(tab)}
                  className={cn(
                    "flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium transition-colors whitespace-nowrap",
                    isActive 
                      ? "bg-brand-soft text-brand font-semibold shadow-xs" 
                      : "text-ink-3 hover:bg-soft hover:text-ink"
                  )}
                >
                  <span>{tab}</span>
                  <span className={cn(
                    "font-mono text-[10px] px-1.5 py-0.2 rounded",
                    isActive ? "bg-brand/15 text-brand" : "bg-soft text-ink-3"
                  )}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right: Search + Agent Filter + Board/Table Switcher + Refresh + Schedule Button */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Smart Search */}
          <div className="relative w-52 sm:w-60">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-3" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search lead, phone, notes..."
              className="w-full rounded-lg border border-line bg-elevated py-1 pl-7 pr-2.5 text-xs text-ink outline-none focus:border-brand transition-colors h-8"
            />
          </div>

          {/* Agent Filter */}
          <Select value={agentFilter} onValueChange={setAgentFilter}>
            <SelectTrigger className="w-[125px] border-line bg-elevated text-xs text-ink-2 h-8">
              <SelectValue placeholder="All agents" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL} className="text-xs">All agents</SelectItem>
              <SelectItem value="unassigned" className="text-xs">Unassigned</SelectItem>
              {members.map((m: any) => (
                <SelectItem key={m.id} value={m.id} className="text-xs">{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Table / Board Switcher */}
          <div className="flex items-center rounded-lg border border-line bg-soft p-0.5">
            <button
              type="button"
              onClick={() => switchView("table")}
              title="Table view"
              className={cn(
                "flex h-7 w-7 items-center justify-center rounded-md text-xs transition-colors",
                viewMode === "table" ? "bg-elevated text-brand shadow-xs font-semibold" : "text-ink-3 hover:text-ink"
              )}
            >
              <TableIcon className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => switchView("board")}
              title="Board view"
              className={cn(
                "flex h-7 w-7 items-center justify-center rounded-md text-xs transition-colors",
                viewMode === "board" ? "bg-elevated text-brand shadow-xs font-semibold" : "text-ink-3 hover:text-ink"
              )}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Refresh */}
          <button 
            onClick={() => void refresh()} 
            title="Refresh follow-ups" 
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-line bg-elevated text-ink-2 hover:bg-soft hover:text-ink transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>

          {/* Schedule Follow-up Button */}
          <button 
            onClick={() => open("new")} 
            className="flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-hover shadow-xs transition-colors h-8 whitespace-nowrap"
          >
            <CalendarPlus className="h-3.5 w-3.5" /> Schedule follow-up
          </button>
        </div>
      </div>

      {(error || query.error || leads.error || team.error) && (
        <p role="alert" className="mt-2 text-xs text-red-600">
          {error || ((query.error || leads.error || team.error) as Error).message}
        </p>
      )}

      {/* ── Content Area ────────────────────────────────────────── */}
      {query.isLoading ? (
        <div className="flex flex-1 items-center justify-center py-16 text-xs text-ink-3">
          Loading follow-ups…
        </div>
      ) : viewMode === "table" ? (
        /* ── TABLE VIEW ─────────────────────────────────────────── */
        <div className="mt-3 flex-1 overflow-auto rounded-xl border border-line bg-elevated shadow-xs">
          <table className="w-full border-collapse text-left text-xs">
            <thead className="sticky top-0 z-10 bg-soft text-[11px] font-semibold uppercase tracking-wider text-ink-3 border-b border-line">
              <tr>
                <th className="whitespace-nowrap px-3.5 py-2.5">Lead</th>
                <th className="whitespace-nowrap px-3.5 py-2.5">Assigned Agent</th>
                <th className="whitespace-nowrap px-3.5 py-2.5">What to discuss</th>
                <th className="whitespace-nowrap px-3.5 py-2.5">Due</th>
                <th className="whitespace-nowrap px-3.5 py-2.5">Status</th>
                <th className="whitespace-nowrap px-3.5 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/60">
              {visibleRows.map(row => {
                const dueDate = new Date(row.dueAt);
                const s = followUpStatus(row);
                const agentName = agentNameOf(row.assignedAgentId);

                return (
                  <tr
                    key={row.id}
                    className="cursor-pointer hover:bg-soft/60 transition-colors group"
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('button, a')) return;
                      openRecord(row.leadId);
                    }}
                  >
                    {/* Lead */}
                    <td className="px-3.5 py-2.5">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className={cn(
                          "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white",
                          avatarColorOf(row.name || row.leadId)
                        )}>
                          {initialsOf(row.name)}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-ink group-hover:text-brand transition-colors">
                            {row.name || "Unnamed lead"}
                          </p>
                          {(row.company || row.phone) && (
                            <div className="flex items-center gap-1.5 text-[11px] text-ink-3">
                              <span>{row.company || row.phone}</span>
                              {row.phone && (
                                <a 
                                  href={`tel:${row.phone}`} 
                                  onClick={e => e.stopPropagation()} 
                                  title="Call phone" 
                                  className="text-ink-4 hover:text-brand"
                                >
                                  <Phone className="h-2.5 w-2.5" />
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Assigned Agent */}
                    <td className="whitespace-nowrap px-3.5 py-2.5">
                      {agentName === "Former agent" ? (
                        <span 
                          className="inline-flex items-center gap-1 rounded-md bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400 border border-amber-500/20" 
                          title="This agent was removed from the organization. Click the pencil icon to reassign to Demo or Aaryan."
                        >
                          Former agent
                        </span>
                      ) : agentName === "Unassigned" ? (
                        <span className="rounded-md bg-soft px-2 py-0.5 text-[10px] font-medium text-ink-3">
                          Unassigned
                        </span>
                      ) : (
                        <div className="flex items-center gap-1.5 text-ink-2">
                          <span className={cn(
                            "flex h-5 w-5 min-w-[20px] shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white shadow-2xs",
                            avatarColorOf(agentName)
                          )}>
                            {initialsOf(agentName)}
                          </span>
                          <span className="truncate max-w-[120px] font-medium text-ink">{agentName}</span>
                        </div>
                      )}
                    </td>

                    {/* Note / What to discuss */}
                    <td className="px-3.5 py-2.5 max-w-[240px]">
                      <p className="truncate text-ink-2" title={row.note}>
                        {row.note || "—"}
                      </p>
                    </td>

                    {/* Due */}
                    <td className="whitespace-nowrap px-3.5 py-2.5 text-ink">
                      <div className="space-y-0.5">
                        <div className="font-medium">
                          {format(dueDate, "MMM d, yyyy · h:mm a")}
                        </div>
                        <div className="text-[11px]">
                          {s === "Overdue" ? (
                            <span className="text-red-600 dark:text-red-400 font-medium">
                              {formatDistanceToNow(dueDate, { addSuffix: true })}
                            </span>
                          ) : s === "Today" ? (
                            <span className="text-brand font-medium">Today</span>
                          ) : (
                            <span className="text-ink-3">
                              {formatDistanceToNow(dueDate, { addSuffix: true })}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Status */}
                    <td className="whitespace-nowrap px-3.5 py-2.5">
                      <span className={cn(
                        "rounded-full px-2 py-0.5 text-[10px] font-semibold",
                        s === "Overdue" ? "bg-red-500/10 text-red-600" :
                        s === "Today" ? "bg-amber-500/10 text-amber-600" :
                        s === "Upcoming" ? "bg-blue-500/10 text-blue-600" :
                        s === "Done" ? "bg-emerald-500/10 text-emerald-600" :
                        "bg-soft text-ink-2"
                      )}>
                        {s}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="whitespace-nowrap px-3.5 py-2.5 text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        {!row.done && (() => {
                          const lead = allLeads.find(l => l.id === row.leadId) || { id: row.leadId, phone: row.phone, name: row.name } as unknown as Lead;
                          return (
                            <LeadCallButton
                              lead={lead}
                              onLogged={() => void refresh()}
                              triggerClassName="flex items-center gap-1 rounded-lg bg-stone-900 text-white hover:bg-stone-800 dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-white px-2.5 py-1 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-40 transition-colors shadow-xs"
                            />
                          );
                        })()}
                        {row.done ? (
                          <button 
                            disabled={busy} 
                            onClick={() => void setDone(row, false)} 
                            className="flex items-center gap-1 rounded-lg bg-soft px-2 py-1 text-xs font-semibold text-ink-2 hover:bg-line disabled:opacity-40 transition-colors"
                          >
                            <RotateCcw className="h-3 w-3" /> Reopen
                          </button>
                        ) : (
                          <>
                            <button 
                              onClick={() => open(row)} 
                              className="rounded p-1 text-ink-3 hover:bg-soft hover:text-ink transition-colors" 
                              title="Reschedule"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button 
                              disabled={busy} 
                              onClick={() => void setDone(row, true)} 
                              className="inline-flex items-center gap-1 rounded bg-emerald-500/10 px-2 py-1 text-xs font-semibold text-emerald-600 hover:bg-emerald-500/20 disabled:opacity-40 transition-colors"
                            >
                              <Check className="h-3 w-3" /> Done
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {visibleRows.length === 0 && (
            <p className="p-8 text-center text-xs text-ink-3">No follow-ups found.</p>
          )}
        </div>
      ) : (
        /* ── KANBAN BOARD VIEW ──────────────────────────────────── */
        <div className="mt-3 flex-1 overflow-hidden pb-2">
          {visibleRows.length === 0 ? (
            <p className="rounded-xl border border-line bg-elevated p-8 text-center text-xs text-ink-3">No follow-ups in this view.</p>
          ) : (
            <div className="flex items-stretch gap-4 overflow-x-auto pb-4 h-full">
              {boardGroups.map((group) => (
                <div
                  key={group.status}
                  onDragOver={(e) => { e.preventDefault(); setDropCol(group.status); }}
                  onDragLeave={() => setDropCol((prev) => (prev === group.status ? null : prev))}
                  onDrop={(e) => {
                    e.preventDefault();
                    const id = e.dataTransfer.getData("text/followup-id") || dragId;
                    if (id) void moveFollowUpToColumn(id, group.status);
                  }}
                  className={cn(
                    "flex w-[280px] shrink-0 flex-col rounded-xl transition-colors",
                    dropCol === group.status ? "bg-brand-soft/60" : "bg-transparent"
                  )}
                >
                  <div className="mb-2 flex shrink-0 items-center gap-2 px-1">
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{
                        backgroundColor:
                          group.status === "Overdue" ? "#dc2626" :
                          group.status === "Today" ? "#ea580c" :
                          group.status === "Upcoming" ? "#06b6d4" : "#16a34a",
                      }}
                    />
                    <h3 className="text-xs font-semibold text-ink">{group.status}</h3>
                    <span className="rounded-full bg-soft px-1.5 py-0.2 text-[10px] font-semibold text-ink-3">{group.items.length}</span>
                  </div>
                  <div className={cn(
                    "flex max-h-[calc(100vh-280px)] min-h-[120px] flex-1 flex-col gap-2.5 overflow-y-auto rounded-xl border border-dashed p-2",
                    dropCol === group.status ? "border-brand bg-brand-soft/40" : "border-line bg-elevated/40"
                  )}>
                    {group.items.length === 0 ? (
                      <p className="p-6 text-center text-xs text-ink-3">Drop a card here</p>
                    ) : (
                      group.items.map((row) => (
                        <div
                          key={row.id}
                          draggable={!busy}
                          onDragStart={(e) => {
                            e.dataTransfer.setData("text/followup-id", row.id);
                            e.dataTransfer.effectAllowed = "move";
                            setDragId(row.id);
                          }}
                          onDragEnd={() => { setDragId(null); setDropCol(null); }}
                          onClick={() => openRecord(row.leadId)}
                          className={cn(
                            "cursor-grab rounded-xl border border-line bg-elevated p-3 shadow-xs hover:border-brand/40 hover:shadow-sm active:cursor-grabbing transition-all",
                            dragId === row.id ? "opacity-50" : "opacity-100"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <div className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white", avatarColorOf(row.leadId))}>
                              {initialsOf(row.name)}
                            </div>
                            <p className="truncate text-xs font-semibold text-ink">{row.name || "Unnamed lead"}</p>
                          </div>
                          {(row.company || row.phone) && (
                            <p className="mt-1 truncate text-[11px] text-ink-3">{row.company || row.phone}</p>
                          )}
                          <p className="mt-1 line-clamp-2 text-xs text-ink-2">{row.note}</p>
                          <div className="mt-2 flex items-center justify-between border-t border-line/60 pt-1.5 text-[10px] text-ink-3">
                            <span className="truncate">{format(new Date(row.dueAt), "MMM d, h:mm a")}</span>
                            {row.assignedAgentId && (
                              <span className="truncate max-w-[100px] font-medium text-ink-2">
                                {agentNameOf(row.assignedAgentId)}
                              </span>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Follow-up Modal ─────────────────────────────────────── */}
      {editing && (
        <Modal
          title={editing === "new" ? "Schedule a follow-up" : "Reschedule follow-up"}
          subtitle="Set a date, a time and what you actually need to discuss."
          width="max-w-md"
          onClose={() => !busy && setEditing(null)}
          footer={
            <>
              <button disabled={busy} onClick={() => setEditing(null)} className="rounded-lg px-4 py-2 text-xs font-semibold text-ink-2 hover:bg-soft transition-colors">Cancel</button>
              <button
                disabled={busy}
                onClick={async () => {
                  if (busy) return;
                  if (!leadId) { setError("Choose a contact."); return; }
                  if (!due || new Date(due).getTime() <= Date.now()) { setError("Choose a future time."); return; }
                  if (!note.trim()) { setError("Add a note about what to discuss."); return; }
                  setBusy(true); setError("");
                  try {
                    if (editing === "new") await crmApi.log(leadId, { kind: "follow-up", followUp: { dueAt: new Date(due).toISOString(), note, assignedAgentId: agentId || null } });
                    else await crmApi.editFollowUp(leadId, editing.id, { done: editing.done, dueAt: new Date(due).toISOString(), note, assignedAgentId: agentId || null });
                    await refresh(); setEditing(null);
                  } catch (e) { setError((e as Error).message); }
                  finally { setBusy(false); }
                }}
                className="rounded-lg bg-brand px-4 py-2 text-xs font-semibold text-white hover:bg-brand-hover disabled:opacity-50 transition-colors shadow-xs"
              >
                {busy ? "Saving…" : editing === "new" ? "Schedule" : "Save"}
              </button>
            </>
          }
        >
          <div className="space-y-3.5">
            <div>
              <label className="mb-1 block text-xs font-semibold text-ink-2">Contact</label>
              <LeadCombobox
                leads={allLeads}
                value={leadId}
                disabled={editing !== "new"}
                onChange={newLeadId => {
                  setLeadId(newLeadId);
                  setAgentId(allLeads.find(l => l.id === newLeadId)?.assignedAgentId || "");
                }}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-ink-2">Assigned Agent</label>
              <select value={agentId} onChange={e => setAgentId(e.target.value)} className="w-full rounded-lg border border-line bg-transparent px-3 py-2 text-xs text-ink outline-none focus:border-brand h-9">
                <option value="">Unassigned</option>
                {members.map((m: any) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-ink-2">Date &amp; time</label>
              <DateTimePicker
                value={due}
                onChange={setDue}
                placeholder="Pick date & time"
                className="w-full"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-ink-2">What to discuss</label>
              <textarea rows={3} maxLength={2000} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Discuss proposal pricing" className="w-full resize-none rounded-lg border border-line bg-transparent px-3 py-2 text-xs text-ink outline-none focus:border-brand" />
            </div>
            {error && <p role="alert" className="text-xs font-semibold text-red-500">{error}</p>}
          </div>
        </Modal>
      )}
    </div>
  );
}
